import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import CartDrawer from '../home/components/CartDrawer';
import Footer from '../home/components/Footer';
import { useCart } from '../../hooks/useCart';
import { useWishlist } from '../../hooks/useWishlist';
import { useSEO } from '../../hooks/useSEO';
import QuickViewModal from './components/QuickViewModal';

interface ShopifyProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  maxPrice: number;
  currencyCode: string;
  image: string;
  imageAlt: string;
  tags: string[];
  tag: string;
  variantId: string | null;
  availableForSale: boolean;
  quantityAvailable: number;
}

export default function ShopPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toggleItem, isWishlisted } = useWishlist();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addedId, setAddedId] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  const [quickViewName, setQuickViewName] = useState('');
  const [wishlistToast, setWishlistToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [categories, setCategories] = useState<string[]>([]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Shop All | Motion Boyz – Luxury Streetwear Collection',
    description: 'Explore the complete Motion Boyz luxury streetwear collection. Shop exclusive hoodies, denim sets, and signature pieces designed for those who never stop moving.',
    canonical: '/shop',
    ogImage: `${siteUrl}/og-shop.jpg`,
    ogType: 'website',
    jsonLd: [
      {
        '@type': 'CollectionPage',
        name: 'Shop All – Motion Boyz',
        description: 'Explore the complete Motion Boyz luxury streetwear collection.',
        url: `${siteUrl}/shop`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Shop', item: `${siteUrl}/shop` },
          ],
        },
      },
      {
        '@type': 'ItemList',
        name: 'Motion Boyz Products',
        url: `${siteUrl}/shop`,
        description: 'Full catalog of Motion Boyz luxury streetwear pieces.',
      },
    ],
  });

  useEffect(() => {
    const handleScrollTop = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScrollTop, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollTop);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [products, selectedCategory, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/shopify-all-products`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      const list: ShopifyProduct[] = data.products || [];
      setProducts(list);
      const cats = [...new Set(list.map((p) => p.category).filter(Boolean))];
      setCategories(cats);
    } catch (err) {
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...products];
    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (sortBy === 'price-low') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => b.price - a.price);
    }
    setFilteredProducts(result);
  };

  const handleAddToCart = (product: ShopifyProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.variantId) return;
    addItem({
      id: product.variantId,
      variantId: product.variantId,
      name: product.name,
      price: product.price,
      image: product.image,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const handleWishlistToggle = (product: ShopifyProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    const wasWishlisted = isWishlisted(product.id);
    toggleItem({
      id: product.id,
      name: product.name,
      price: product.price,
      currencyCode: product.currencyCode,
      image: product.image,
      category: product.category,
      availableForSale: product.availableForSale,
      variantId: product.variantId,
    });
    const message = wasWishlisted ? 'Removed from wishlist' : 'Saved to wishlist ♥';
    setWishlistToast({ message, visible: true });
    setTimeout(() => setWishlistToast({ message: '', visible: false }), 2200);
  };

  const openQuickView = (e: React.MouseEvent, product: ShopifyProduct) => {
    e.stopPropagation();
    setQuickViewId(extractNumericId(product.id));
    setQuickViewName(product.name);
  };

  const extractNumericId = (gid: string) => gid.split('/').pop() || '';

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-black">

        {/* Hero Banner */}
        <div className="relative w-full h-[420px] overflow-hidden pt-16">
          <img
            src="https://public.readdy.ai/ai/img_res/6f1a2bafa78a41bd087d916d2208088a.jpg"
            alt="Shop All Collection"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="inline-flex items-center gap-2 px-4 py-1 border border-[#00d4ff]/40 rounded-full mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00d4ff] animate-pulse"></span>
              <span className="text-[#00d4ff] text-xs tracking-widest uppercase">Full Collection</span>
            </div>
            <h1
              className="text-6xl md:text-7xl font-black tracking-widest uppercase mb-4 text-white"
              style={{ fontFamily: 'Rajdhani, sans-serif', textShadow: '0 0 40px rgba(0,212,255,0.5)' }}
            >
              Shop All
            </h1>
            <p className="text-gray-300 text-sm tracking-wider max-w-xl">
              Power Doesn&apos;t Announce Itself
            </p>
          </div>
        </div>

        <div className="w-[90%] max-w-[1400px] mx-auto pb-24 pt-10">

          {/* Filter & Sort Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 p-4 bg-gradient-to-r from-[#0a0a0a] to-[#050505] border border-[#00d4ff]/10 rounded-2xl">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-gray-500 text-xs tracking-widest uppercase hidden sm:block">Filter:</span>
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? 'bg-[#00d4ff] text-black font-semibold'
                    : 'border border-[#00d4ff]/20 text-gray-400 hover:border-[#00d4ff]/50 hover:text-white'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-[#00d4ff] text-black font-semibold'
                      : 'border border-[#00d4ff]/20 text-gray-400 hover:border-[#00d4ff]/50 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-black/50 border border-[#00d4ff]/20 rounded-full text-gray-300 text-xs tracking-wider uppercase cursor-pointer hover:border-[#00d4ff]/40 transition-colors duration-300 focus:outline-none focus:border-[#00d4ff]"
            >
              <option value="newest">Newest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>

          {/* Product Count */}
          {!loading && !error && (
            <div className="mb-6 flex items-center gap-2">
              <span className="w-1 h-4 bg-[#00d4ff] rounded-full"></span>
              <p className="text-gray-500 text-xs tracking-widest uppercase">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'}
              </p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32 gap-5">
              <div className="w-14 h-14 border-4 border-[#00d4ff]/20 border-t-[#00d4ff] rounded-full animate-spin"></div>
              <p className="text-gray-500 text-xs tracking-widest uppercase">Loading Collection...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-32 gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-full border border-red-500/30 text-red-400">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <p className="text-gray-400 text-sm tracking-wider">{error}</p>
              <button
                onClick={fetchProducts}
                className="px-6 py-2 bg-[#00d4ff] text-black text-xs tracking-widest uppercase font-semibold rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32 gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-full border border-[#00d4ff]/20 text-[#00d4ff]/40">
                <i className="ri-shopping-bag-line text-3xl"></i>
              </div>
              <p className="text-gray-500 text-sm tracking-widest uppercase">No products found</p>
              <button
                onClick={() => setSelectedCategory('all')}
                className="px-6 py-2 border border-[#00d4ff]/30 text-[#00d4ff] text-xs tracking-widest uppercase rounded-full hover:bg-[#00d4ff]/10 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* Product Grid */}
          {!loading && !error && filteredProducts.length > 0 && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-product-shop
            >
              {filteredProducts.map((product) => {
                const wishlisted = isWishlisted(product.id);
                return (
                  <div
                    key={product.id}
                    onClick={() => navigate(`/product/${extractNumericId(product.id)}`)}
                    className="group relative bg-gradient-to-br from-[#0d0d0d] to-[#060606] border border-white/5 rounded-2xl overflow-hidden cursor-pointer hover:border-[#00d4ff]/30 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,212,255,0.12)]"
                  >
                    {/* Image */}
                    <div className="relative w-full h-80 overflow-hidden bg-[#0a0a0a]">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.imageAlt || product.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-700">
                          <i className="ri-image-line text-5xl"></i>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Tag badge */}
                      {product.tag && (
                        <div className="absolute top-3 left-3 px-2 py-0.5 bg-black/60 border border-[#00d4ff]/30 rounded-full">
                          <span className="text-[#00d4ff] text-[9px] tracking-widest uppercase">{product.tag}</span>
                        </div>
                      )}

                      {/* Wishlist button — always visible, top-right */}
                      <button
                        onClick={(e) => handleWishlistToggle(product, e)}
                        aria-label={wishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
                        className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap z-10 ${
                          wishlisted
                            ? 'bg-rose-500/90 border-rose-400 text-white shadow-[0_0_14px_rgba(244,63,94,0.6)]'
                            : 'bg-black/50 border-white/20 text-gray-300 hover:bg-rose-500/80 hover:border-rose-400 hover:text-white backdrop-blur-sm'
                        }`}
                      >
                        <i className={`text-sm ${wishlisted ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                      </button>

                      {/* Sold Out badge */}
                      {!product.availableForSale && (
                        <div className="absolute bottom-3 left-3 px-2 py-0.5 bg-black/80 border border-white/20 rounded-full">
                          <span className="text-gray-400 text-[9px] tracking-widest uppercase">Sold Out</span>
                        </div>
                      )}

                      {/* Hover action buttons */}
                      <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                        {/* Quick View button */}
                        <button
                          onClick={(e) => openQuickView(e, product)}
                          className="px-5 py-2 text-xs tracking-widest uppercase font-semibold rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
                        >
                          <i className="ri-eye-line mr-1.5"></i>Quick View
                        </button>
                        {/* Add to Cart button */}
                        <button
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={!product.availableForSale || !product.variantId}
                          className={`px-6 py-2 text-xs tracking-widest uppercase font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed ${
                            addedId === product.id
                              ? 'bg-green-400 text-black'
                              : 'bg-[#00d4ff] text-black hover:bg-white'
                          }`}
                          style={addedId !== product.id ? { boxShadow: '0 0 20px rgba(0,212,255,0.5)' } : {}}
                        >
                          {addedId === product.id
                            ? '✓ Added'
                            : product.availableForSale
                            ? 'Add to Cart'
                            : 'Sold Out'}
                        </button>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      {product.category && (
                        <p className="text-[#00d4ff] text-[10px] tracking-widest uppercase mb-1.5 opacity-70">
                          {product.category}
                        </p>
                      )}
                      <h3 className="text-white text-sm font-semibold mb-2 tracking-wide group-hover:text-[#00d4ff] transition-colors duration-300 leading-snug">
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <p className="text-gray-300 text-sm font-light">
                          ${product.price.toFixed(2)}{' '}
                          <span className="text-gray-600 text-xs">{product.currencyCode}</span>
                        </p>
                        <div className="w-7 h-7 flex items-center justify-center rounded-full border border-white/10 text-gray-500 group-hover:border-[#00d4ff]/40 group-hover:text-[#00d4ff] transition-all duration-300">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
      <CartDrawer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 z-50 w-11 h-11 flex items-center justify-center rounded-full bg-[#00d4ff] text-black shadow-[0_0_20px_rgba(0,212,255,0.5)] transition-all duration-300 cursor-pointer whitespace-nowrap ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <i className="ri-arrow-up-line text-lg font-bold"></i>
      </button>

      {/* Wishlist Toast */}
      <div
        className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-full bg-[#111] border border-rose-500/40 text-white text-xs tracking-widest uppercase shadow-[0_0_20px_rgba(244,63,94,0.3)] flex items-center gap-2 transition-all duration-400 whitespace-nowrap ${
          wishlistToast.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
        }`}
      >
        <i className="ri-heart-fill text-rose-400"></i>
        {wishlistToast.message}
      </div>

      {/* Quick View Modal */}
      {quickViewId && (
        <QuickViewModal
          productId={quickViewId}
          productName={quickViewName}
          onClose={() => setQuickViewId(null)}
        />
      )}
    </>
  );
}
